import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';

import DA from '../store/ReduxAccess';

import CrmTextField from '../components/CrmTextField';
import CrmButton from '../components/CrmButton';

import { updateOutreach } from '../store/slices/outreachListsSlice';
import { updateMissive } from '../store/slices/missivesSlice';
import { updateFirmOutreach } from '../store/slices/outreachesSlice';
import { maybeUpdatePersonLatestOutreachInfo } from '../store/slices/personsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import ReduxLoader from '../store/ReduxLoader';

import activityLoggingPanelStyles from '../assets/jss/crm/components/activityLoggingPanelStyles';

import KSelect from './KSelect';
import LabeledTimePicker from './LabeledTimePicker';
import KDatePicker from './KDatePicker';

/*
 * Call with something like this:
    <ActivityEditorPanel
      activity={activity}
      page={<somebody that implements buildActivityGroupsFromReduxStore()>}
      activityColumn={null}
      client={client}
      referrerOrgId={0}
      contacts={contacts}
      deleteFunc={(activity) => someFunctionToDeleteActivity(activity)}
      closeEditorFunc={() => someFunctionToCloseThisEditor()}
    />
 */

class ActivityEditorPanel extends React.Component {
  constructor(props) {
    super(props);
    const now = DateTime.local();
    const startOfToday = now.startOf('day');
    this.state = {
      activityType: 'Phone Call',
      activityDirection: 'To',
      activityPartner: 0,
      activityDescription: '',
      activityDate: startOfToday,
      activityTime: now.diff(startOfToday), // will probably be overridden by componentDidMount or componentDidUpdate
    };
  }

  componentDidMount() {
    this.populateFromProps();
  }

  componentDidUpdate(prevProps) {
    if (this.props.activity !== prevProps.activity) {
      this.populateFromProps();
    }
  }

  populateFromProps() {
    /*
     * State to populate
     *   activityType
     *   activityDirection ("To" or "From")
     *   activityPartner (ID)
     *   activityDescription
     *   activityDate (Luxon DateTime for start of day)
     *   activityTime (24-hour-based Luxon Duration)
     */

    const { activityMode, direction, agentId, subjectId, content, dateTime } = this.props.activity;
    const activityTypeStr = this.mapActivityModeToActivityTypeStr(activityMode);

    const activityPartner = direction === 'To' ? subjectId : agentId;
    const startOfDate = dateTime.startOf('day');
    this.setState({
      activityType: activityTypeStr,
      activityDirection: direction,
      activityPartner: activityPartner,
      activityDescription: content,
      activityDate: startOfDate,
      activityTime: dateTime.diff(startOfDate),
    });
  }

  mapActivityModeToActivityTypeStr(activityMode) {
    switch (activityMode) {
      case 'phone':
        return 'Phone Call';
      case 'email':
        return 'Email';
      case 'voicemail':
        return 'Voice Mail';
      case 'meeting':
        return 'Meeting';
      case 'tour':
        return 'Tour';
      case 'scheduleAssessment':
        return 'Schedule Assessment';
      case 'completeAssessment':
        return 'Complete Assessment';
      default:
        return 'Phone Call';
    }
  }

  inferDirection() {
    // TODO really implement this
    // const thisFirmId = DA.getFirmId();
    // return activity.receivingFirm === thisFirm ? "From" : "To";
    return 'To';
  }

  handleSelectChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleActivityDateChange(newDate) {
    this.setState({ activityDate: newDate });
  }

  handleActivityTimeChange(newDuration) {
    this.setState({ activityTime: newDuration });
  }

  cancelEditing() {
    this.props.closeEditorFunc();
  }

  deleteActivity() {
    this.props.deleteFunc(this.props.activity);
  }

  submitActivityModification() {
    switch (this.state.activityType) {
      case 'Phone Call':
        this.submitModificationOfActivity('PhoneCall');
        break;
      case 'Email':
        this.submitModificationOfActivity('PersonalEmail');
        break;
      case 'Voice Mail':
        this.submitModificationOfActivity('PersonalVoiceMailOrMessage');
        break;
      case 'Meeting':
      case 'Tour':
        this.submitModificationOfActivity(this.state.activityType);
        break;
      case 'Schedule Assessment':
        this.submitModificationOfActivity('ScheduleAssessment');
        break;
      case 'Complete Assessment':
        this.submitModificationOfActivity('CompleteAssessment');
        break;
      default:
        break;
    }
  }

  buildApiStructForModifyingActivity(typeStr) {
    const userPersonId = this.props.userInfo.personId;
    const contactId = this.state.activityPartner;
    const activity = this.props.activity;
    const thisFirmId = DA.getFirmId();
    const otherFirmId = this.props.referrerOrgId;
    const activityDay = this.state.activityDate.startOf('day');
    const activityDate = activityDay.plus(this.state.activityTime);
    const direction = this.state.activityDirection;
    const senderId = direction === 'To' ? userPersonId : contactId;
    const senderFirmId = direction === 'To' ? thisFirmId : otherFirmId;
    const receiverId = direction === 'To' ? contactId : userPersonId;
    const receiverFirmId = direction === 'To' ? otherFirmId : thisFirmId;
    const missiveDescription = this.state.activityDescription.trim();
    const missiveContent = missiveDescription;

    return {
      outreachId: activity.id,
      date: activityDate.toISO(),
      firmId: thisFirmId,
      senderId,
      senderFirmId,
      receiverId,
      receiverFirmId,
      dlId: 0,
      missiveId: activity.missiveId,
      missive: {
        missiveId: activity.missiveId,
        firmId: thisFirmId,
        creatorId: senderId,
        typeStr,
        name: this.state.activityType,
        description: missiveDescription,
        content: missiveContent,
      },
    };
  }

  submitModificationOfActivity(typeStr) {
    const activityId = this.props.activity.id;
    const thisFirmId = DA.getFirmId();
    const apiStruct = this.buildApiStructForModifyingActivity(typeStr);
    const uri = `/api/firm/${thisFirmId}/outreach/${activityId}`;
    const request = createStdRequest(uri, 'put', apiStruct);
    const actionOnGoodResponse = (json) => {
      const outreach = json;
      const missive = outreach.missive;
      this.props.updateMissive(missive);
      const otherFirmId = this.props.referrerOrgId;
      const isOrgOutreach = otherFirmId;
      const reduxStruct = ReduxLoader.convertOutreachStructIntoReduxStruct(outreach);
      if (isOrgOutreach) {
        this.props.updateFirmOutreach(reduxStruct);
      } else {
        const receiverId = this.state.activityPartner ? this.state.activityPartner : this.props.contacts[0].personId;
        this.props.updateOutreach({ personId: receiverId, outreach: reduxStruct });
      }
      if (this.props.activityColumn) {
        this.props.activityColumn.buildActivityGroupsFromReduxStore(otherFirmId);
      } else if (this.props.page) {
        const client = this.props.client;
        if (client) {
          const clientId = client.personId;
          this.props.page.buildActivityGroupsFromReduxStore(clientId);
        } else {
          this.props.page.buildActivityGroupsFromReduxStore(otherFirmId);
        }
      } else {
        // TODO This will crash, since this.props.page is guaranteed not to be a page
        // Don't know what the original intention was
        const partyId = this.props.client ? this.props.client.personId : this.state.activityPartner;
        this.props.page.buildActivityGroupsFromReduxStore(partyId);
      }
      this.props.maybeUpdatePersonLatestOutreachInfo({
        personId: outreach.receiverId,
        date: outreach.date,
        description: missive.description,
      });
      this.props.closeEditorFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes, contacts } = this.props;
    const contactNames = contacts ? contacts.map((c) => c.preferredFullName) : [];
    const contactIds = contacts ? contacts.map((c) => c.personId) : [];
    const firstContactId = contactIds.length > 0 ? contactIds[0] : 0;
    const activityPartnerToShow = this.state.activityPartner ? this.state.activityPartner : firstContactId;

    const { dropdowns } = this.props;
    const activityTypeDropdownSpec = dropdowns.find((dd) => dd.name === 'Activity Type');
    const activityTypeDropdownOptions = activityTypeDropdownSpec.items.map((item) => item.text);
    const activityType = this.state.activityType;
    const requiresToFromChoice = !(
      activityType === 'Meeting' ||
      activityType === 'Tour' ||
      activityType === 'Schedule Assessment' ||
      activityType === 'Complete Assessment'
    );

    return (
      <div className={classes.activityLoggingPanel}>
        <div className={classes.activitySelectDiv}>
          <KSelect
            id="activity-select"
            name="activityType"
            fullWidth={true}
            liveOptions={activityTypeDropdownOptions}
            value={activityType}
            onChange={(event) => this.handleSelectChange(event)}
          />
        </div>
        {requiresToFromChoice ? (
          <div className={classes.activityDirectionDiv}>
            <KSelect
              id="activity-direction"
              name="activityDirection"
              fullWidth={true}
              liveOptions={['To', 'From']}
              value={this.state.activityDirection}
              onChange={(event) => this.handleSelectChange(event)}
            />
          </div>
        ) : (
          <div className={classes.activityLiteralWithDiv}>With</div>
        )}

        <div className={classes.activityPartnerDiv}>
          <KSelect
            id="activity-partner"
            name="activityPartner"
            fullWidth={true}
            liveOptions={contactNames}
            liveValues={contactIds}
            value={activityPartnerToShow}
            onChange={(event) => this.handleSelectChange(event)}
          />
        </div>

        <CrmTextField
          fullWidth
          multiline
          value={this.state.activityDescription}
          onChange={(event) => this.setState({ activityDescription: event.target.value })}
        />

        <div className={classes.dateTimePanelComponentDateDiv}>
          <KDatePicker
            label="Date"
            value={this.state.activityDate}
            onChange={(newDate) => this.handleActivityDateChange(newDate)}
          />
        </div>
        <LabeledTimePicker
          label="Time"
          value={this.state.activityTime}
          onChange={(newDuration) => this.handleActivityTimeChange(newDuration)}
        />

        <div className={classes.buttonsDiv}>
          <CrmButton outlined size="small" onClick={() => this.deleteActivity()}>
            Delete
          </CrmButton>
          <CrmButton outlined size="small" onClick={() => this.cancelEditing()}>
            Cancel
          </CrmButton>

          <CrmButton filled size="small" onClick={() => this.submitActivityModification()}>
            Save
          </CrmButton>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  dropdowns: state.firmInfo.dropdowns,
});
const actionProducers = {
  setApiResponseInfo,
  updateOutreach,
  updateMissive,
  updateFirmOutreach,
  maybeUpdatePersonLatestOutreachInfo,
};
const styledClass = withStyles(activityLoggingPanelStyles)(ActivityEditorPanel);
export default connect(mapStateToProps, actionProducers)(styledClass);
